import React, {useCallback, useEffect, useRef} from 'react';
import {ListBoxOptionType, Text} from '@flixbus/honeycomb-react';
import {useBusPartner} from '@/Pages/PartnerCosts/hooks/useBusPartner';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {ProductionCostBusScheduleProvidedValueEntry} from '@/Pages/ProductionCost/store/types';
import {useProductionCostBus} from '@/Pages/ProductionCost/components/DataImportStepTwo/hooks/useProductionCostBus';
import {useLocationStateAndReset} from '../../../hooks/useLocationStateAndReset';
import PartnerNameAutocompleteField from './PartnerNameAutocompleteField/PartnerNameAutocompleteField';
import {PartnerNameProps} from './PartnerName.types';
import useDriverDefaults from './useDriverDefaults';
import style from './PartnerName.module.scss';

const PartnerName: React.FC<PartnerNameProps> = ({
  vehicleId,
  busPartnerId,
  entryIds,
  errors,
  onUpdateBusValues,
  onUpdateBusScheduleValues
}) => {
  const hasRunOnceRef = useRef(false);

  const busPartner = useBusPartner(busPartnerId ?? null);
  const partners = useAppSelector(({partners}) => partners.list ?? []);
  const productionCostBuses = useProductionCostBus(vehicleId);

  const countryDriverConceptTypes = useAppSelector(
    ({productionCost}) => productionCost.countryDriverConceptTypes
  );

  const locationState = useLocationStateAndReset<{
    isNewProductionCost: boolean;
  }>();

  const {getDriverDefaults} = useDriverDefaults(entryIds);

  const handleSelectPartnerName = useCallback(
    (option: ListBoxOptionType) => {
      const busPartnerId = option.value as string;
      const busPartnerCountryId = partners.find(
        ({id}) => id === busPartnerId
      )?.countryId;

      if (!busPartnerCountryId) {
        console.error('Country not found for bus partner', busPartnerId);
        return;
      }

      onUpdateBusValues({
        busPartnerId,
        countryRulesCountryId: busPartnerCountryId,
        busPartnerBusTypeCostEntryId: undefined,
        countryBusTypeCostEntryId: undefined
      });

      const updates: Partial<ProductionCostBusScheduleProvidedValueEntry> = {};

      const driverDefaults = getDriverDefaults(
        busPartnerId,
        busPartnerCountryId
      );
      if (driverDefaults) {
        updates.accommodationCostPerNight =
          driverDefaults.accommodationCostPerNight;
        updates.dailyAllowance = driverDefaults.dailyAllowance;
        updates.emptyHrPerDay = driverDefaults.prefilledEmptyHr;
        updates.emptyKmPerDay = driverDefaults.prefilledEmptyDistance;
      }

      const driverConcepts =
        countryDriverConceptTypes[busPartnerCountryId] ?? [];
      const countryDriverConceptTypeId =
        driverConcepts?.length === 1 && driverConcepts[0].id
          ? driverConcepts[0].id
          : undefined;
      updates.countryDriverConceptTypeId = countryDriverConceptTypeId;

      onUpdateBusScheduleValues(updates);
    },
    [
      countryDriverConceptTypes,
      getDriverDefaults,
      partners,
      onUpdateBusValues,
      onUpdateBusScheduleValues
    ]
  );

  // Runs setup logic for the selected partner on initial load if it's a new production cost
  useEffect(() => {
    if (
      busPartner &&
      locationState?.isNewProductionCost &&
      !hasRunOnceRef.current
    ) {
      hasRunOnceRef.current = true;

      handleSelectPartnerName({
        value: busPartner.id,
        title: busPartner.name
      });
    }
  }, [busPartner, handleSelectPartnerName, locationState]);

  return (
    <div className={style.container} data-testid={`line-vehicle-partner-name`}>
      <PartnerNameAutocompleteField
        partners={partners}
        busPartner={busPartner}
        vehicleId={vehicleId}
        errors={errors}
        onSelectPartnerName={handleSelectPartnerName}
      />

      <Text small extraClasses="content-secondary-color">
        Planr partner name: {productionCostBuses?.busPartnerName ?? 'N/A'}
      </Text>
    </div>
  );
};

export default React.memo(PartnerName);
